import { useCallback } from "react";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { serverErrorMessage } from "../lib/apiError";
import { getCsrfHeader } from "../lib/csrf";
import type { InviteStatus } from "./useInvites";
import { useCachedQuery } from "./useCachedQuery";

// Mirrors AdminInviteView on the backend. `instanceCount` is what makes an
// invite "live": zero means the customer hasn't turned it into an OpenClaw
// yet, so the invite is still ours to chase.
export type AdminInvite = {
  email: string;
  status: InviteStatus;
  invitedAt: string;
  invitedBy: string;
  acceptedAt: string | null;
  tier: string | null;
  requestedIntegrations: ReadonlyArray<string>;
  instanceCount: number;
};

export type AdminInvitesStatus = "loading" | "ready" | "error";

export type AdminInviteResult<T> = { ok: true; value: T } | { ok: false; message: string };

export type UseAdminInvitesResult = {
  status: AdminInvitesStatus;
  invites: ReadonlyArray<AdminInvite>;
  errorMessage: string | null;
  isFetching: boolean;
  refetch: () => Promise<void>;
  createInvite: (email: string, integrations: ReadonlyArray<string>) => Promise<AdminInviteResult<AdminInvite>>;
  setIntegrations: (email: string, integrations: ReadonlyArray<string>) => Promise<AdminInviteResult<void>>;
  resendInvite: (email: string) => Promise<AdminInviteResult<void>>;
  revokeInvite: (email: string) => Promise<AdminInviteResult<void>>;
};

const LIST_URL = ADMIN_QUERIES.invites.url;

const fetchInvites = async (signal: AbortSignal): Promise<ReadonlyArray<AdminInvite>> => {
  const res = await fetch(LIST_URL, { credentials: "include", signal });
  if (!res.ok) throw new Error(`Failed to load invites (${res.status})`);
  return (await res.json()) as ReadonlyArray<AdminInvite>;
};

const jsonHeaders = (): Record<string, string> => ({
  "Content-Type": "application/json",
  ...getCsrfHeader(),
});

export const useAdminInvites = (): UseAdminInvitesResult => {
  const { status, data, error, isFetching, refetch, mutate } = useCachedQuery<ReadonlyArray<AdminInvite>>({
    cacheResource: ADMIN_QUERIES.invites.resource,
    queryKey: LIST_URL,
    fetcher: fetchInvites,
  });
  const invites = data ?? [];

  // POST returns the InviteView shape (no instanceCount — a brand-new
  // invitee owns nothing), so the row is completed locally rather than
  // triggering a list refetch whose failure would blank the table.
  const createInvite = useCallback(
    async (email: string, integrations: ReadonlyArray<string>): Promise<AdminInviteResult<AdminInvite>> => {
      try {
        const res = await fetch(LIST_URL, {
          method: "POST",
          credentials: "include",
          headers: jsonHeaders(),
          body: JSON.stringify({ email, integrations }),
        });
        if (res.status === 201) {
          const created = (await res.json()) as Omit<AdminInvite, "instanceCount">;
          const row: AdminInvite = { ...created, instanceCount: 0 };
          mutate((prev) => [row, ...prev.filter((i) => i.email !== row.email)]);
          return { ok: true, value: row };
        }
        return { ok: false, message: await serverErrorMessage(res, "Invite failed") };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [mutate],
  );

  // Write-through from the server's own copy of the set (it normalizes and
  // de-dupes), so the row never shows an optimistic value the backend didn't
  // actually store.
  const setIntegrations = useCallback(
    async (email: string, integrations: ReadonlyArray<string>): Promise<AdminInviteResult<void>> => {
      try {
        const res = await fetch(`${LIST_URL}/${encodeURIComponent(email)}/integrations`, {
          method: "PUT",
          credentials: "include",
          headers: jsonHeaders(),
          body: JSON.stringify({ integrations }),
        });
        if (res.status === 200) {
          const updated = (await res.json()) as Omit<AdminInvite, "instanceCount">;
          mutate((prev) =>
            prev.map((i) =>
              i.email === updated.email ? { ...i, requestedIntegrations: updated.requestedIntegrations } : i,
            ),
          );
          return { ok: true, value: undefined };
        }
        return { ok: false, message: await serverErrorMessage(res, "Save failed") };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [mutate],
  );

  const resendInvite = useCallback(async (email: string): Promise<AdminInviteResult<void>> => {
    try {
      const res = await fetch(`${LIST_URL}/${encodeURIComponent(email)}/resend`, {
        method: "POST",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });
      if (res.status === 204) return { ok: true, value: undefined };
      return { ok: false, message: await serverErrorMessage(res, "Resend failed") };
    } catch (err) {
      return { ok: false, message: err instanceof Error ? err.message : String(err) };
    }
  }, []);

  const revokeInvite = useCallback(
    async (email: string): Promise<AdminInviteResult<void>> => {
      try {
        const res = await fetch(`${LIST_URL}/${encodeURIComponent(email)}`, {
          method: "DELETE",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        });
        if (res.status === 204) {
          mutate((prev) => prev.map((i) => (i.email === email ? { ...i, status: "REVOKED" } : i)));
          return { ok: true, value: undefined };
        }
        return { ok: false, message: await serverErrorMessage(res, "Revoke failed") };
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : String(err) };
      }
    },
    [mutate],
  );

  return {
    status,
    invites,
    errorMessage: error,
    isFetching,
    refetch,
    createInvite,
    setIntegrations,
    resendInvite,
    revokeInvite,
  };
};
